import { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";

// Helpers
import { maxHeight } from "@Common/helpers/visuals";

// Components
import { CustomPopper } from "@Layouts/components/CustomPopper";

// MUI
import { Chip, Paper, TextField, Autocomplete, ListItem, Tooltip, Box } from "@mui/material";

// Icons
import ClearIcon from "@mui/icons-material/Clear";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const resolveDefaultProps = (oldProps) => {
	const {
		options = [],
		value = [],
		disableClearable = false,
		onSelectOption = () => {},
		onRemoveOption = () => {},
		onClear = () => {},
		onSettled = () => {},
		onProgress = () => {},
	} = oldProps;
	return {
		...oldProps,
		options,
		value,
		disableClearable,
		onSelectOption,
		onRemoveOption,
		onClear,
		onSettled,
		onProgress,
	};
};

export default function OemMultiAdvancedSelect(oldProps) {
	const props = resolveDefaultProps(oldProps);
	const [value, setValue] = useState([]);
	const [open, setOpen] = useState(false);
	const [placeholder, setPlaceholder] = useState("");

	const options = useMemo(() => {
		switch (value.length) {
			case 0:
				return props.options;
			case 1:
				// eslint-disable-next-line no-case-declarations
				const [oemObj] = value;
				if (props.hideSubmenu || oemObj?.hideSubmenu) {
					props?.setOptionsCallBack({
						oem: oemObj?.oemName,
						oemReference: oemObj?.shortName,
						dealerCodeFS: oemObj?.dealerCodeFS,
					});
					return [];
				}

				return oemObj?.franchises.map((item) => ({
					label: item.label,
					hasOptions: false,
				}));
			case 2:
				// eslint-disable-next-line no-case-declarations
				const [oem, franchise] = value;
				props?.setOptionsCallBack({
					oem: oem?.oemName,
					oemReference: oem?.shortName,
					dealerCodeFS: oem?.dealerCodeFS,
					franchise: franchise?.label,
				});
				return [];
			default:
				return [];
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	function setPlaceholderString(placeHolderString) {
		if (!placeHolderString) {
			return setPlaceholder("");
		}
		return setPlaceholder(`   ${placeHolderString}`);
	}

	const eventsEmitter = (event, newValue, reason) => {
		setPlaceholder("");
		switch (reason) {
			case "selectOption":
				if (!value.includes(newValue.label)) {
					setValue([...value, newValue]);
				}
				props.onSelectOption(newValue.label);
				break;
			case "removeOption":
				setValue(newValue);
				if (value.length === 1) {
					props.onClear(value[0]);
					props?.placeholder && setPlaceholderString(props?.placeholder);
				} else {
					props.onRemoveOption(newValue[0].label);
				}
				break;
			case "clear":
				setValue([]);
				props?.placeholder && setPlaceholderString(props?.placeholder);
				break;
			case "settled":
				if (!value.includes(newValue.label)) {
					setValue([...value, newValue]);
					props.onSettled([...value, newValue]);
				}
				break;
			default:
				break;
		}
	};

	useEffect(() => {
		setValue([]);
		setOpen(false);
		props?.placeholder && setPlaceholderString(props?.placeholder);
	}, [props?.placeholder, props.tab]);

	useEffect(() => setPlaceholderString(props.placeholder), [props.placeholder]);

	return (
		<Paper elevation={0} sx={{ width: "100%", backgroundColor: "transparent" }}>
			<Autocomplete
				disabled={props.disabled}
				open={!options?.length ? false : open}
				onOpen={() => setOpen(true)}
				onClose={() => setOpen(false)}
				onChange={eventsEmitter}
				disablePortal
				multiple
				options={options}
				value={value}
				renderInput={(params) => (
					<TextField
						{...params}
						className={clsx({
							[props.className]: true,
							"advance-select-open": open,
							"advance-select-expand-area": props.disableClearable,
							"advanced-select": true,
							"regular-input-text": true,
						})}
						InputProps={{
							...params.InputProps,
							readOnly: true,
							style: {
								cursor: "pointer",
								caretColor: "transparent",
							},
							onKeyDown: (event) => event.preventDefault(),
						}}
						placeholder={placeholder}
						type="text"
					/>
				)}
				renderTags={(tagValue, getTagProps) =>
					tagValue.map((option, index) => {
						const { key, ...restProps } = getTagProps({ index });
						return (
							<Tooltip key={option?.label} title={(props?.tooltip && props?.tooltip[option?.label]) || ""}>
								<Chip
									key={key}
									{...restProps}
									deleteIcon={index !== value.length - 1 ? <></> : <ClearIcon />}
									label={option.label}
									size="small"
									sx={{
										borderRadius: "4px",
										width: props?.chipWidth || option?.hideSubmenu ? "200px" : "90px",
										justifyContent: "space-between",
										fontSize: "14.5px",
										fontFamily: "var(--font-family-v2)",
										color: "var(--text-color)",
									}}
								/>
							</Tooltip>
						);
					})
				}
				renderOption={(renderOptionProps, option) => {
					const { key, ...restProps } = renderOptionProps;

					// Helper functions to simplify conditionals
					const getEventMessage = () => (option.hasOptions || !value?.length ? "selectOption" : "settled");

					const isOptionDisabled = () => props.disabledMarketShares.includes(option.label) && props.tab === "Market Share";

					const shouldShowArrowIcon = () => (!value.length || option.hasOptions) && !props?.hideSubmenu && !option.hideSubmenu;

					const getPositionsText = () => (option?.positions ? ` (${option.positions.join(", ")})` : "");

					const eventMessage = getEventMessage();
					const isDisabled = isOptionDisabled();
					const positionsText = getPositionsText();
					const showArrowIcon = shouldShowArrowIcon();

					const handleClick = (event) => {
						if (isDisabled) {
							event.preventDefault();
							event.stopPropagation();
							return;
						}
						eventsEmitter(event, option, eventMessage);
					};

					const listItemStyles = {
						backgroundColor: "white !important",
						display: "flex !important",
						alignItems: "center !important",
						justifyContent: "space-between !important",
						opacity: isDisabled ? 0.5 : 1,
						cursor: isDisabled ? "not-allowed" : "pointer",
						"&:hover": {
							backgroundColor: isDisabled ? "white !important" : "var(--gray-background) !important",
						},
					};

					const tooltipTitle = isDisabled ? props.errorMessages[props.tab] : "";

					return (
						<Tooltip key={key} title={tooltipTitle}>
							<ListItem {...restProps} onClick={handleClick} sx={listItemStyles}>
								<Box component="span">
									{option.label}
									{positionsText}
								</Box>
								{showArrowIcon && (
									<ArrowForwardIosIcon
										sx={{
											fontSize: "14.5px",
											color: "var(--text-color)",
										}}
									/>
								)}
							</ListItem>
						</Tooltip>
					);
				}}
				isOptionEqualToValue={() => true}
				getOptionLabel={(option) => option.label}
				sx={{
					...props.sx,
					minWidth: "100%",
					"& .MuiInputBase-root": {
						padding: "2px 30px 2px 2px !important",
					},
					"& .MuiAutocomplete-tag": {
						margin: "0px 0px 0px 1px",
					},
				}}
				PopperComponent={CustomPopper}
				popupIcon={<ArrowForwardIosRoundedIcon size="small" sx={{ padding: "4px", transform: "rotate(90deg)" }} />}
				ListboxProps={{
					className: "advanced-select-dropdown",
					sx: { maxHeight: maxHeight(props.items) },
				}}
			/>
		</Paper>
	);
}

OemMultiAdvancedSelect.propTypes = {
	options: PropTypes.array,
	value: PropTypes.array,
	disableClearable: PropTypes.bool,
	onSelectOption: PropTypes.func,
	onRemoveOption: PropTypes.func,
	onClear: PropTypes.func,
	onSettled: PropTypes.func,
	onProgress: PropTypes.func,
	placeholder: PropTypes.string,
	tooltip: PropTypes.object,
	errorMessages: PropTypes.object,
	disabledMarketShares: PropTypes.array,
	tab: PropTypes.string,
	hideSubmenu: PropTypes.bool,
	setOptionsCallBack: PropTypes.func,
	clear: PropTypes.bool,
	disabled: PropTypes.bool,
	className: PropTypes.string,
	sx: PropTypes.object,
	chipWidth: PropTypes.bool,
	items: PropTypes.array,
};
